import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiV1 } from "../lib/axios";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import { removeTokens, storeTokens, getAccessToken } from "../utils/sessionMethods";
import { isDevelopment } from "..";

export const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [isLogged, setIsLogged] = useState(false);
  const [loginLoader, setLoginLoader] = useState(false);
  const [user, setUser] = useState({});
  const [isFirstAccess, setIsFirstAccess] = useState(false);
  const [isFirstAccessConfirmed, setIsFirstAccessConfirmed] = useState(false);
  const [firstAccessLoader, setFirstAccessLoader] = useState(false);
  const [phone, setPhone] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [companyUser, setCompanyUser] = useState();
  const [step, setStep] = useState(1);

  const [firstLoginPass, setFirstLoginPass] = useState(false);

  const navigate = useNavigate();

  const account = user.account;

  // Recarrega perfil automaticamente ao montar se já houver token (ex: F5)
  const [profileLoading, setProfileLoading] = useState(!!getAccessToken());

  useEffect(() => {
    if (getAccessToken()) {
      getProfile().finally(() => setProfileLoading(false));
    }
  }, []);

  async function login(taxNumber, password, email, captchaToken, recaptchaRef, loginType) {
    setLoginLoader(true);
    const data = {
      taxNumber,
      password,
      email,
      loginType,
      captchaToken,
    };

    await apiV1
      .post("/login/session", data)
      .then((response) => {
        setPhone(response.data.phone);
        setCompanyUser(response.data.companyUser);

        setIsFirstAccess(response.data.isFirstAccess);

        if(response.data.hasOwnProperty('status')) {
          toast.warning(response.data.message);
          return;
        }

        if (!response.data.isFirstAccess) {
          setFirstLoginPass(true);
        }

        setTaxNumber(taxNumber);
        setLoginLoader(false);
        setStep(2);
      })
      .catch((err) => {
        if(err?.response?.status === 409) {
          toast.error(getApiErrorMessage(err, 'Erro ao realizar login'));
        } else {
          toast.error(getApiErrorMessage(err, 'Ocorreu um erro nos servidores, tente novamente!'));
        }

        if (!isDevelopment) recaptchaRef.reset();
        
        setLoginLoader(false);
      });
  }

  async function login2FA(cod) {
    setLoginLoader(true);

    await apiV1
      .post("/login/authenticate", {
        taxNumber,
        otp: cod,
        companyUserId: companyUser?.id,
      })
      .then(async (response) => {
        setIsLogged(true);

        storeTokens(
          response.data.accessToken,
          response.data.refreshToken,
        );

        await getProfile();

        navigate("/pagina-principal");
      })
      .catch((err) => {
        if(err?.response?.status === 409) {
          toast.error(getApiErrorMessage(err, 'Erro ao autenticar'));
        } else {
          toast.error(getApiErrorMessage(err, 'Ocorreu um erro nos servidores, tente novamente!'));
        }
      })
      .finally(() => {
        setLoginLoader(false);
      });
  }

  async function logout() {
    setFirstLoginPass(false);
    removeTokens();
    setIsLogged(false);
    localStorage.clear();
    setUser({});
    setStep(1);
    navigate("/");
  }

  async function getProfile() {
    await apiV1.get(`/person/show`).then((response) => {
      const result = response.data.data;

      if(result.companyUser) localStorage.setItem("companyUserId", result.companyUser?.id);

      localStorage.setItem("name", result.companyUser?.name || result.name);
      localStorage.setItem("taxNumber", result.taxNumber);
      localStorage.setItem("personId", result.id);
      localStorage.setItem("accountId", result.account.id);
      localStorage.setItem("bank", result.account.bank);
      localStorage.setItem("bankAccount", result.account.bankAccount);
      localStorage.setItem("bankAccountDigit", result.account.bankAccountDigit);
      localStorage.setItem("bankBranch", result.account.bankBranch);
      localStorage.setItem("login", result.login);

      setUser(result);
    });
  }

  async function resendLogin2FA(type) {
    try {
      await apiV1.post("/login/resend-code", {
        taxNumber,
        type,
        companyUserId: companyUser?.id
      })
      type === 'email' && toast.success('Enviamos o código para seu e-mail cadastrado');
    } catch (error) {
      console.log("erro resendLogin2FA", error);
      toast.error(getApiErrorMessage(error, 'Erro ao reenviar código'));
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isLogged,
        loginLoader,
        user,
        account,
        profileLoading,
        isFirstAccess,
        isFirstAccessConfirmed,
        firstAccessLoader,
        phone,
        firstLoginPass,
        step,
        login,
        login2FA,
        logout,
        setIsFirstAccess,
        setIsFirstAccessConfirmed,
        getProfile,
        setFirstLoginPass,
        setStep,
        resendLogin2FA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
